import { ref } from 'vue';
import { NavigationGuard } from 'vue-router';
import EnvironmentService from '@/env/EnvironmentService';
import { FpAxios } from '@/services/FpAxios';
import { resolveLendiAiBaseUrl } from '@/utils/lendiAiBaseUrl';

type AccessResponse = {
  success: boolean;
  data?: { canAccess: boolean };
};

/**
 * Without one, entering „Raporty" would hang for as long as lendi-ai takes to
 * answer — the guard below waits on this request before the route opens.
 */
const ACCESS_TIMEOUT_MS = 3000;

/**
 * „Raporty" fronts a Polish-only sales dashboard, so the Romanian DSA tenant
 * must not see the entry — same guard the `my-expenses` and
 * `legal-request-*` entries use.
 *
 * The only half of the rule that lives here. Who may see the funnel is decided
 * by lendi-ai alone (reports admin, or anyone the warehouse records a case for
 * as a director); mirroring those roles here is what let the two drift — a
 * reports admin holding no CRM admin role and leading no division was approved
 * by lendi-ai and hidden by this file anyway.
 */
const isLendiPlTenant = (): boolean => EnvironmentService.Environment.isAppDomain().LENDI_PL;

/**
 * Demo points `VUE_APP_LENDI_AI_URL` at the QA deployment, which validates the
 * forwarded token against the QA API and so answers 401 to a demo-tenant one.
 * The CRM's global 401 handler reads that as a dead session and logs the user
 * out, so demo must not ask at all — and with nothing to show for it, the entry
 * stays hidden, which is what a demo without the report should render anyway.
 *
 * `window.__ENV__` (set from the hostname in `windowConfig`) is the only demo
 * signal the app has — `EnvironmentEnum` has no DEMO member — and it is what
 * `useIframeOrigins` already reads for the same distinction.
 */
const isDemoEnvironment = (): boolean => window.__ENV__ === 'demo';

/** Where „Raporty" can be offered at all, before lendi-ai gets a say. */
const isFunnelOfferable = (): boolean => isLendiPlTenant() && !isDemoEnvironment();

/** `null` until lendi-ai answers. Hidden while it is. */
const hasFunnelToShow = ref<boolean | null>(null);
let pendingAnswer: Promise<void> | null = null;

export const canAccessDivisionReports = (): boolean => isFunnelOfferable() && hasFunnelToShow.value === true;

/** Test seam: the verdict is module state, so a suite has to clear it. */
export function resetDivisionReportsAccess(): void {
  hasFunnelToShow.value = null;
  pendingAnswer = null;
}

async function askLendiAi(): Promise<void> {
  // `forceDev: true` matches GoToService.getLendiAiUrl, so this asks the same
  // origin that will serve the iframe rather than a different deployment.
  const baseUrl = resolveLendiAiBaseUrl({ forceDev: true, });

  try {
    const response = await FpAxios.create({ clientToken: true, })
      .get<AccessResponse>(`${baseUrl}/api/sales/analytics/home-funnel/access`, { timeout: ACCESS_TIMEOUT_MS, });
    hasFunnelToShow.value = response.data?.data?.canAccess ?? false;
  } catch (error) {
    // Fails closed. Nothing filters this in front any more, so failing open
    // would put „Raporty" in front of every expert in the CRM whenever
    // lendi-ai is unreachable — and the report behind it would not render.
    console.error('Nie udało się sprawdzić dostępu do lejka Home — „Raporty" zostają ukryte', error);
    hasFunnelToShow.value = false;
  }
}

/**
 * Resolves the verdict, once. The navigation guard and the CRM's navigation
 * both want it, and neither should pay for a second round-trip;
 * `canAccessDivisionReports` is a plain query over the result, so the
 * navigation computed re-evaluates on its own when this lands.
 */
export function ensureDivisionReportsAccess(): Promise<void> {
  if (!isFunnelOfferable()) return Promise.resolve();

  pendingAnswer ??= askLendiAi();

  return pendingAnswer;
}

/**
 * Holds the route until the verdict is in.
 *
 * `meta.condition` is not only a navigation-menu filter: `checkRouteAccess`
 * reads it in the router's `afterEach` and pushes the user back to the
 * dashboard when it answers false. Since the verdict starts pending, entering
 * without waiting would throw a director off the page they opened from a
 * bookmark or a reload.
 *
 * Enters plainly either way — refusal is the router's own job, and it already
 * answers a blocked route with the standard „brak dostępu" snackbar.
 */
export const divisionReportsGuard: NavigationGuard = async(to, from, next) => {
  await ensureDivisionReportsAccess();
  next();
};
